import { useQuery } from '@tanstack/react-query';
import { Stack, Card, Group, Text, Title, Button, Skeleton, Badge } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader, StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import { flows } from '@repletoryreach/sdk-api';
import { formatInteger, formatRelative } from '../../../lib/format';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

const SOURCE_TONE: Record<flows.LlmProvider['source'], StatusTone> = {
  registry: 'healthy',
  config: 'warning',
  none: 'down'
};

const SOURCE_LABEL_KEYS: Record<flows.LlmProvider['source'], string> = {
  registry: 'media.kb.provider.sourceRegistry',
  config: 'media.kb.provider.sourceConfig',
  none: 'media.kb.provider.sourceNone'
};

/**
 * The knowledge-base page was thirteen lines of placeholder for all four of
 * the things it promised. Three of them genuinely await a cross-tenant
 * endpoint, but `GET /v1/llm/provider` is shipped, platform-admin only and
 * answers the first-order question the placeholder hid: which model this
 * deployment is actually talking to, and whether it is still the offline stub.
 * That is rendered here, with the placeholder kept below it for the parts that
 * really are pending.
 */
// The delivery reference for the pending half of this page. It is internal
// jargon, so it is shown with a legend rather than on its own.
const WORKSTREAM = 'W20'; // i18n-ignore: internal workstream code

export default function MediaKbPage() {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'llm', 'provider'],
    queryFn: () => flows.getLlmProvider(),
    staleTime: 5 * 60_000
  });
  const provider = data?.data;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('media.kb.eyebrow')}
        title={
          <>
            {t('media.kb.title')}{' '}
            <Tooltip label={t('gateways.workstreamTooltip', { workstream: WORKSTREAM })} position="top">
              <Badge size="sm" variant="light" color="orange">{WORKSTREAM}</Badge>
            </Tooltip>
          </>
        }
        description={t('media.kb.provider.description')}
        actions={
          <Stack gap="xs" align="flex-end">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
              {t('action.refresh')}
            </Button>
            <Text role="caption" c={MUTED}>
              {t('media.kb.provider.lastChecked', { when: formatRelative(new Date(dataUpdatedAt).toISOString()) })}
            </Text>
          </Stack>
        }
      />

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Title order={4}>{t('media.kb.provider.title')}</Title>
            {provider && (
              <StatusBadge status={SOURCE_TONE[provider.source]}>
                {t(SOURCE_LABEL_KEYS[provider.source])}
              </StatusBadge>
            )}
          </Group>

          {isLoading && <Skeleton height={96} />}

          {error && (
            <Alert color="error" title={t('media.kb.provider.loadError')}>
              <Group gap="sm">
                <Text role="bodySmall">{error instanceof Error ? error.message : t('common.unknownError')}</Text>
                <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
              </Group>
            </Alert>
          )}

          {provider && (
            <Stack gap="md">
              {/* Four facts across, so the provider, the two models and where
                  the credentials came from are read in one line rather than
                  as a wrapping cluster. */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                <Stack gap="xs">
                  <Text role="caption" c={MUTED}>{t('media.kb.provider.chat')}</Text>
                  <Text role="bodySmall" fw={600}>{provider.chat.name}</Text>
                  <Text role="caption" c={MUTED}>{provider.chat.model}</Text>
                </Stack>
                <Stack gap="xs">
                  <Text role="caption" c={MUTED}>{t('media.kb.provider.embedding')}</Text>
                  <Text role="bodySmall" fw={600}>{provider.embedding.name}</Text>
                  <Text role="caption" c={MUTED}>{provider.embedding.model}</Text>
                </Stack>
                <Stack gap="xs">
                  <Text role="caption" c={MUTED}>{t('media.kb.provider.dimension')}</Text>
                  <Text role="bodySmall" fw={600}>{formatInteger(provider.embedding.dimension)}</Text>
                </Stack>
                <Stack gap="xs">
                  <Text role="caption" c={MUTED}>{t('media.kb.provider.source')}</Text>
                  <Text role="bodySmall" fw={600}>{t(SOURCE_LABEL_KEYS[provider.source])}</Text>
                </Stack>
              </div>
              {provider.source === 'none' && (
                <Alert color="warning" variant="light">{t('media.kb.provider.stubWarning')}</Alert>
              )}
            </Stack>
          )}
        </Stack>
      </Card>

      <Alert color="info" title={t('common.pendingBackendFor', { workstream: 'W20' })}>
        {t('media.kb.description')}
      </Alert>
    </Stack>
  );
}
